export interface BrandingSettings {
    primaryColor: string;
    clubName: string;
    logoUrl: string;
    pageTitle: string;
    faviconUrl: string;
    sidebarBg: string;
    submenuBg: string;
    sidebarItemSelectedBg: string;
    heroImageUrl: string;
    backgroundImageUrl: string;
    tagline: string;
    membershipCardBg: string;
}

// Club contact details. Mirrors the backend ContactSettings record — every
// field is optional free text (empty string = not configured). Email,
// supportEmail, website and phone are format-validated on write; clients
// created before FORT-127 may only have email populated.
export interface ContactSettings {
    email: string;
    phone: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    postcode: string;
    country: string;
    website: string;
    supportEmail: string;
}

export interface LocaleSettings {
    currency: string;
    timezone: string;
}

export interface TaxSettings {
    vatRate: number;
}

export interface HoursSettings {
    openingJson: string;
}

// Structured config the API stores/serves as raw JSON strings (it never parses
// them — only the frontend does). Mirrors the backend FeesSettings/FormsSettings/
// BannerSettings/CheckinSettings records. Parse the *Json fields with the helpers
// in components/membership/forms and profile/bannerRules before use.
export interface FeesSettings {
    joiningJson: string;
}

export interface FormsSettings {
    healthJson: string;
    newMemberJson: string;
    signupJson: string;
}

export interface BannerSettings {
    rulesJson: string;
}

// Mirrors the backend CheckinSettings record.
//   birthdayMessageJson    — opaque greeting config (parse before use).
//   duplicateWindowMinutes — repeat-scan window in minutes. A scan within this many
//                            minutes of the same member's previous scan is the same
//                            visit, so the check-in report counts it once. Reporting
//                            only: every scan is still recorded, and check-in is never
//                            refused. 0 counts every scan.
export interface CheckinSettings {
    birthdayMessageJson: string;
    duplicateWindowMinutes: number;
}

// Backend CheckinSettings.Default / the bounds it validates against. Used when a
// client predates the setting, and to keep the editor's limits in step with the API's.
export const DEFAULT_CHECKIN_DUPLICATE_WINDOW_MINUTES = 5;
export const MIN_CHECKIN_DUPLICATE_WINDOW_MINUTES = 0;
export const MAX_CHECKIN_DUPLICATE_WINDOW_MINUTES = 1440;

// Per-client direct-debit config. Mirrors the backend DirectDebitSettings record.
//   cancellationNotice — free-text notice a member reads and agrees to (via a tick
//                        box) when setting up their direct-debit mandate. Empty =>
//                        no notice is shown (the feature is off for that club).
export interface DirectDebitSettings {
    cancellationNotice: string;
}

// Per-client hourly booking prices (FORT-288). Mirrors the backend
// BookingPricingSettings record. A booking is charged whole hours x hourlyRate
// plus a part-hour charge chosen by each facility type's own partHourCharging:
//   'proRata'       — the part hour is charged pro-rata of the hourly rate.
//   'partHourPrice' — the facility type's fixed partHourPrice is added (a type
//                     without one falls back to pro-rata).
// A facility type with no rate keeps the legacy slot pricing. The frontend only
// edits these settings: prices come from the API (GET /bookings/price-quote,
// BookingService.getPriceQuote) — see docs/adr/0002-server-side-booking-pricing.md.
export type PartHourCharging = 'proRata' | 'partHourPrice';

export interface FacilityTypeRate {
    // FacilityType enum name, e.g. 'Squash', 'Studio', 'TreatmentRoom'.
    facilityType: string;
    hourlyRate: number;
    partHourPrice: number | null;
    partHourCharging: PartHourCharging;
}

export interface BookingPricingSettings {
    // An API that predates per-type modes sends rates without partHourCharging and
    // this club-wide mode instead; the current API never sends it.
    facilityTypeRates: (Omit<FacilityTypeRate, 'partHourCharging'> & { partHourCharging?: PartHourCharging })[];
    partHourCharging?: PartHourCharging;
}

export const DEFAULT_BOOKING_PRICING: BookingPricingSettings = {
    facilityTypeRates: [],
};

// Per-client billing-cycle anchoring. Mirrors the backend BillingSettings record.
//   monthlyCycleDay  — day of month a monthly membership runs to / direct debits
//                      collect on. null means "last day of month".
//   proRataCutoffDay — join-day threshold; joining on/after it rolls a new
//                      member's first period forward an extra cycle. Default 23.
export interface BillingSettings {
    monthlyCycleDay: number | null;
    proRataCutoffDay: number;
}

// Per-client membership-card PDF layout. Mirrors the backend
// MembershipCardSettings record (strongly typed both sides). The card generator
// renders straight off this; a client with no stored layout gets
// DEFAULT_MEMBERSHIP_CARD_LAYOUT, which reproduces the current Southbank card.
export type MembershipCardHeaderType = 'text' | 'logo';
export type MembershipCardFieldType = 'memberName' | 'membershipId' | 'companyName';

export interface MembershipCardHeader {
    type: MembershipCardHeaderType;
    // null => fall back to branding.membershipCardBg.
    backgroundColor: string | null;
    // S3 object key (private assets bucket) of the header logo used when
    // type === 'logo'. The card generator fetches it as base64 via the API
    // (getMembershipCardLogoData). null => no card logo. Card-specific: distinct
    // from branding.logoUrl, which drives the app-shell favicon/manifest.
    logoKey: string | null;
}

export interface MembershipCardField {
    type: MembershipCardFieldType;
    // null => no label prefix; the value renders on its own.
    label: string | null;
    // When true, the field (and its label) is omitted if the member has no value.
    hideWhenEmpty: boolean;
}

export interface MembershipCardBarcode {
    show: boolean;
    format: string;
    source: string;
}

export interface MembershipCardLayout {
    version: number;
    showPhoto: boolean;
    header: MembershipCardHeader;
    fields: MembershipCardField[];
    barcode: MembershipCardBarcode;
}

// A single selectable option on a signup/enquiry form dropdown. Mirrors the
// backend SignupOptionSettings record. `value` is what gets persisted (a
// member-metadata code, or a GuestType enum name for the guest/enquiry lists);
// `label` is what the operator sees.
export interface SignupOption {
    value: string;
    label: string;
}

// Per-client signup-form config. Mirrors the backend SignupSettings record.
//   membershipPricingSheetKey — S3 object key (private assets bucket) of a pricing
//                         sheet image the guest form renders (via a presigned URL)
//                         when a Tour guest is signed up. Empty string hides it.
//   interests / howTheyHeard / guestTypes / enquiryTypes — the per-client option
//                         lists for the guest and tour signup forms. Absent keys
//                         fall back server-side to the built-in defaults below.
export interface SignupSettings {
    collectCompanyName: boolean;
    membershipPricingSheetKey: string;
    interests: SignupOption[];
    howTheyHeard: SignupOption[];
    guestTypes: SignupOption[];
    enquiryTypes: SignupOption[];
    // FORT-434 / FORT-456: members signing up online must set up a Direct Debit —
    // the "skip for now" option is removed. Optional only for an API that predates it.
    directDebitRequired?: boolean;
}

// Per-client cancellation config. Mirrors the backend CancellationSettings record.
//   reasons — the option list offered when a membership is cancelled. Values are
//             persisted to member_cancellations.reason and grouped by the retention
//             report, so they must stay stable once in use; labels can be reworded.
export interface CancellationSettings {
    reasons: SignupOption[];
}

export const DEFAULT_CANCELLATION_REASONS: SignupOption[] = [
    { value: 'MovingArea', label: 'Moving area' },
    { value: 'Finances', label: 'Finances' },
    { value: 'DidNotLikeGym', label: "Didn't like the gym" },
    { value: 'FoundAnotherGym', label: 'Found another gym' },
    { value: 'DirectDebitRejected', label: 'Direct Debit Rejected' },
    { value: 'Other', label: 'Other' },
];

// The GuestType enum names a guest/enquiry-type option value may take. Kept in
// sync with the backend GuestType enum (Unknown excluded — it's the not-set
// sentinel). Constrains the guest/enquiry list editor so the lead-source and
// follow-up branching keyed off these values keeps working.
export const GUEST_TYPE_VALUES = ['Squash', 'Fitness', 'Tour', 'Call'] as const;

// Defaults reproduce the lists previously hardcoded in the signup screens.
// Used as the fallback when a client has no stored override, and to seed the
// settings editor. Kept in sync with the backend SignupSettings.Default*.
export const DEFAULT_SIGNUP_INTERESTS: SignupOption[] = [
    { value: 'Gym', label: 'Gym' },
    { value: 'Squash', label: 'Squash' },
    { value: 'Classes', label: 'Classes' },
    { value: 'Steam / Sauna', label: 'Steam / Sauna' },
    { value: 'Treatments', label: 'Treatments' },
];

/**
 * HowTheyHeard codes that mean "someone referred me", and so open the referring-member capture on the
 * signup forms.
 *
 * Two codes, not one: FORT-349 retired the dedicated 'Member' option in favour of 'FriendFamily'
 * ("Referred by a friend"), but a client whose signup settings were saved before that still serves
 * 'Member' in their configured list. Matching on either keeps the referral sub-form reachable for both,
 * so no client silently loses ReferringMember capture on the day their config differs from the defaults.
 */
export const REFERRAL_HOW_THEY_HEARD_VALUES: readonly string[] = ['FriendFamily', 'Member'];

/** True when a HowTheyHeard selection should open the referring-member capture. Tolerates unset. */
export const isReferralHowTheyHeard = (value: unknown): boolean =>
    typeof value === 'string' && REFERRAL_HOW_THEY_HEARD_VALUES.includes(value);

// Labels follow the client-agreed "How They Heard About Us" wording; the values are the codes the signup
// forms have always persisted and must not change, or the retention panel orphans historic members.
export const DEFAULT_SIGNUP_HOW_THEY_HEARD: SignupOption[] = [
    { value: 'WalkingBy', label: 'Walking by' },
    { value: 'SocialMedia', label: 'Social media' },
    { value: 'WebsiteGoogle', label: 'Website' },
    { value: 'FriendFamily', label: 'Referred by a friend' },
    { value: 'Other', label: 'Other' },
];

export const DEFAULT_SIGNUP_GUEST_TYPES: SignupOption[] = [
    { value: 'Fitness', label: 'Fitness' },
    { value: 'Squash', label: 'Squash' },
];

export const DEFAULT_SIGNUP_ENQUIRY_TYPES: SignupOption[] = [
    { value: 'Tour', label: 'Tour' },
    { value: 'Call', label: 'Call' },
];

export interface ClientSettings {
    branding: BrandingSettings;
    contact: ContactSettings;
    locale: LocaleSettings;
    tax: TaxSettings;
    hours: HoursSettings;
    fees: FeesSettings;
    forms: FormsSettings;
    banners: BannerSettings;
    checkin: CheckinSettings;
    billing: BillingSettings;
    membershipCard: MembershipCardLayout;
    signup: SignupSettings;
    cancellation: CancellationSettings;
    directDebit: DirectDebitSettings;
    // Optional on the wire-type so a config from an API that predates FORT-288
    // (or a partial test fixture) still prices bookings the legacy way.
    bookingPricing?: BookingPricingSettings;
}

// Mirrors the backend MembershipCardSettings.Default — the layout used when a
// client has no stored membershipCard.layout. Reproduces the previously
// hardcoded Southbank card (photo, text header, name + "Member #:" id, barcode).
export const DEFAULT_MEMBERSHIP_CARD_LAYOUT: MembershipCardLayout = {
    version: 1,
    showPhoto: true,
    header: { type: 'text', backgroundColor: null, logoKey: null },
    fields: [
        { type: 'memberName', label: null, hideWhenEmpty: false },
        { type: 'membershipId', label: 'Member #:', hideWhenEmpty: false },
    ],
    barcode: { show: true, format: 'CODE128', source: 'membershipId' },
};

export interface ClientConfig {
    clientId: number;
    settings: ClientSettings;
    // null until a Fortius admin assigns one via the Identity section.
    subdomainSlug?: string | null;
}
